import {
  ActiveTariff,
  AddOn,
  EmploymentStatus,
} from '@getpopsure/private-health-insurance-pricing-engine';

export type Modal = {
  title: string;
  children: string | React.ReactNode;
};

export interface ModalWithTracking extends Modal {
  tariff: TariffsWithPublic | 'header';
  fieldKey: string;
}

export const mapEmploymentStatus: Record<EmploymentStatus, string> = {
  EMPLOYED_IN_GERMANY: 'Employed',
  EMPLOYED_OUTSIDE_OF_GERMANY: 'Employed',
  SELF_EMPLOYED: 'Self employed',
  OTHER: 'Other',
} as const;

export type Tariff = ActiveTariff;
export type TariffsWithPublic = ActiveTariff | 'Public';

export type QuestionnaireId =
  | 'dateOfBirth'
  | 'citizenship'
  | 'arrivalDateToGermany'
  | 'stayingMoreThanTenYears'
  | 'employmentStatus'
  | 'employmentStatusOther'
  | 'studentType'
  | 'income'
  | 'employmentOutsideGermany'
  | 'healthStatus'
  | 'healthCondition'
  | 'familyCoverage'
  | 'tariffInfo'
  | 'addOns'
  | 'starting';

export const questionnaireOrder: QuestionnaireId[] = [
  'dateOfBirth',
  'citizenship',
  'arrivalDateToGermany',
  'stayingMoreThanTenYears',
  'employmentStatus',
  'employmentStatusOther',
  'studentType',
  'income',
  'employmentOutsideGermany',
  'healthStatus',
  'healthCondition',
  'familyCoverage',
  'tariffInfo',
  'addOns',
  'starting',
];

export interface AddonData {
  id: AddOn;
  title: string;
  description: React.ReactNode;
  options?: React.ReactNode;
  /** This will show a modal in the Tariff Details section */
  moreInfo?: React.ReactNode;
}

export type AddonsInfo = { [k in AddOn]: Omit<AddonData, 'id'> };

export type ValueWithDescription = {
  value: boolean | string;
  description?: string;
};

export interface TableData {
  birthYear?: number;
  income?: number;
  isAboveIncomeThreshold?: boolean;
  isFamily?: boolean;

  id: number;
  name: string;
  tariff?: Tariff;
  tariffKey: TariffsWithPublic;
  contribution?: number | string;
  cashback?: Tariff | boolean;
  deductible: string | boolean;
  deductibleInfo?: string;
  dependents: boolean | string;
  dependentsInfo?: string;

  /** general */
  generalDoctor: string | boolean;
  specialist: string | boolean;
  specialistInfo?: string;
  medication: string | boolean;
  medicationInfo?: string;
  vaccination: string | boolean;
  vaccinationInfo?: string;
  transports: string | boolean;
  transportsInfo?: string;
  sickPayCoverage: string;
  sickPayCoverageInfo?: string;
  digitalHealthApps: string | boolean;
  digitalHealthAppsInfo?: string;
  nonMedicalPrescriptions: boolean;
  purelyCosmeticProcedures: boolean;

  /** dental */
  annualCoverage: string;
  annualCoverageInfo?: string;
  dentalCheckups: string | boolean;
  dentalCleanings: ValueWithDescription;
  dentist: boolean | string;
  inlays: boolean | string;
  inlaysInfo?: string;
  dentures: boolean | string;
  denturesInfo?: string;
  orthodonticsChildren: boolean | string;
  orthodonticsChildrenInfo?: string;

  /** treatment and therapies */
  preventiveCare: boolean | string;
  preventiveCareInfo?: string;
  therapeutic: string | boolean;
  therapeuticInfo?: string;
  mentalHealth: string | boolean;
  mentalHealthInfo?: string;
  radioTherapy: string | boolean;
  natural: string | boolean;
  naturalInfo?: string;
  medicalAids: string;
  medicalAidsInfo?: string;

  /** vision */
  visionAids: string | boolean;
  visionAidsInfo?: string;
  refractiveSurgery: string | boolean;
  refractiveSurgeryInfo?: string;

  /** pregnancy */
  pregnancy: string | boolean;
  pregnancyInfo?: string;
  fertilityTreatment: string | boolean;
  fertilityTreatmentInfo?: string;
  midwife: string | boolean;
  midwifeInfo?: string;
  nonMedicalPregnancyServices: string | boolean;
  nonMedicalPregnancyServicesInfo?: string;

  /** hospital */
  boardAndRoom: string;
  privateHospitals: boolean | string;
  privateHospitalsInfo?: string;
  headPhysician: string | boolean;
  headPhysicianInfo?: string;

  /** abroad */
  euWideCover: string | boolean;
  euWideCoverInfo?: string | boolean;
  repatriation: boolean | string;
  worldwideCover: boolean;
  worldwideCoverInfo?: string;
}

export const deductibleLabelsMapping: Record<TariffsWithPublic, string> = {
  NKSelectS: 'with €0 deductible',
  NKSelectL: 'with €0 deductible',
  NKSelectXL: 'with €0 deductible',
  Public: 'with €0 deductible',
  HiMedical: '10% deductible',
  HiMedicalPlus: '10% deductible',
};

export const deductibleInfoMapping: Record<Tariff, string> = {
  NKSelectS: '',
  NKSelectL: '',
  NKSelectXL: '',
  HiMedical:
    'You’ll pay 10% of each bill out of pocket. Once you’ve paid €500 out of pocket per year, the deductible no longer applies. You’ll be covered as foreseen by your insurance plan.',
  HiMedicalPlus:
    'You’ll pay 10% of each bill out of pocket. Once you’ve paid €500 out of pocket per year, the deductible no longer applies. You’ll be covered as foreseen by your insurance plan.',
};
